// ---------------------------------------------------------------------------
// Report form – full bug-report form UI
// ---------------------------------------------------------------------------

import type { ResolvedConfig } from "../core/config";
import type { ApiClient } from "../core/api-client";
import { sdkState } from "../core/state";

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------

interface LocaleStrings {
  title: string;
  categoryLabel: string;
  categoryPlaceholder: string;
  titleLabel: string;
  titlePlaceholder: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  screenshotLabel: string;
  screenshotDrop: string;
  screenshotHint: string;
  submit: string;
  submitting: string;
  successMessage: string;
  errorMessage: string;
  required: string;
  maxFiles: string;
  maxSize: string;
  invalidType: string;
}

const LOCALE: Record<"ko" | "en", LocaleStrings> = {
  ko: {
    title: "버그 리포트",
    categoryLabel: "카테고리",
    categoryPlaceholder: "카테고리 선택 (선택사항)",
    titleLabel: "제목",
    titlePlaceholder: "어떤 문제가 발생했나요?",
    descriptionLabel: "설명",
    descriptionPlaceholder:
      "문제를 자세히 설명해 주세요. 재현 순서가 있다면 함께 알려주세요.",
    screenshotLabel: "스크린샷",
    screenshotDrop: "클릭하거나 파일을 드래그하세요",
    screenshotHint: "최대 3장 · JPG, PNG, WebP · 5MB 이하",
    submit: "제출하기",
    submitting: "제출 중...",
    successMessage: "티켓이 접수되었습니다. 감사합니다!",
    errorMessage: "제출에 실패했습니다. 잠시 후 다시 시도해 주세요.",
    required: "필수 항목입니다",
    maxFiles: "최대 3장까지 업로드 가능합니다",
    maxSize: "파일 크기는 5MB를 초과할 수 없습니다",
    invalidType: "JPG, PNG, WebP 파일만 가능합니다",
  },
  en: {
    title: "Bug Report",
    categoryLabel: "Category",
    categoryPlaceholder: "Select a category (optional)",
    titleLabel: "Title",
    titlePlaceholder: "What went wrong?",
    descriptionLabel: "Description",
    descriptionPlaceholder:
      "Please describe the issue in detail. Include steps to reproduce if possible.",
    screenshotLabel: "Screenshots",
    screenshotDrop: "Click or drag files here",
    screenshotHint: "Max 3 files · JPG, PNG, WebP · 5 MB each",
    submit: "Submit",
    submitting: "Submitting...",
    successMessage: "Your ticket has been submitted. Thank you!",
    errorMessage: "Submission failed. Please try again later.",
    required: "This field is required",
    maxFiles: "You can upload up to 3 files",
    maxSize: "File size cannot exceed 5 MB",
    invalidType: "Only JPG, PNG, WebP files are allowed",
  },
};

const MAX_FILES = 3;
const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

// ---------------------------------------------------------------------------
// Close icon SVG
// ---------------------------------------------------------------------------

const CLOSE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
</svg>`;

const UPLOAD_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
  class="pp-file-input__icon">
  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
  <polyline points="17 8 12 3 7 8"/>
  <line x1="12" y1="3" x2="12" y2="15"/>
</svg>`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string>,
  ...children: (string | Node)[]
): HTMLElementTagNameMap[K] {
  const elem = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      elem.setAttribute(k, v);
    }
  }
  for (const c of children) {
    if (typeof c === "string") elem.appendChild(document.createTextNode(c));
    else elem.appendChild(c);
  }
  return elem;
}

function collectBrowserInfo(): Record<string, unknown> {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    screenWidth: screen.width,
    screenHeight: screen.height,
    url: location.href,
  };
}

// ---------------------------------------------------------------------------
// Cleanup reference for global listeners
// ---------------------------------------------------------------------------

let documentKeydownHandler: ((e: KeyboardEvent) => void) | null = null;

export function destroyReportForm(): void {
  if (documentKeydownHandler) {
    document.removeEventListener("keydown", documentKeydownHandler);
    documentKeydownHandler = null;
  }
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function createReportForm(
  shadowRoot: ShadowRoot,
  apiClient: ApiClient,
  config: ResolvedConfig,
): HTMLElement {
  const t = LOCALE[config.locale];
  const files: File[] = [];

  // ---- Overlay ----
  const overlay = el("div", {
    class: `pp-form-overlay${config.position === "bottom-left" ? " pp-form-overlay--left" : ""}`,
    role: "dialog",
    "aria-modal": "true",
    "aria-label": t.title,
  });

  // ---- Container ----
  const container = el("div", { class: "pp-form-container" });

  // ---- Header ----
  const header = el("div", { class: "pp-form-header" });
  const titleEl = el("span", { class: "pp-form-header__title" }, t.title);
  const closeBtn = el("button", {
    class: "pp-form-header__close",
    type: "button",
    "aria-label": config.locale === "en" ? "Close" : "닫기",
  });
  closeBtn.innerHTML = CLOSE_ICON;
  header.append(titleEl, closeBtn);

  // ---- Body ----
  const body = el("div", { class: "pp-form-body" });

  // Category select
  const categoryField = el("div", { class: "pp-field" });
  const categoryLabel = el(
    "label",
    { class: "pp-label", for: "pp-category" },
    t.categoryLabel,
  );
  const categorySelect = el("select", {
    class: "pp-select",
    id: "pp-category",
    "aria-label": t.categoryLabel,
  });
  const defaultOpt = el("option", { value: "" }, t.categoryPlaceholder);
  categorySelect.appendChild(defaultOpt);
  categoryField.append(categoryLabel, categorySelect);

  // Populate categories
  loadCategories(apiClient, categorySelect);

  // Title input
  const titleField = el("div", { class: "pp-field" });
  const titleLabel = el(
    "label",
    { class: "pp-label pp-label--required", for: "pp-title" },
    t.titleLabel,
  );
  const titleInput = el("input", {
    class: "pp-input",
    id: "pp-title",
    type: "text",
    maxlength: "200",
    placeholder: t.titlePlaceholder,
    required: "true",
    "aria-required": "true",
  });
  const titleError = el("div", { class: "pp-field-error", "aria-live": "polite" });
  titleError.style.display = "none";
  titleField.append(titleLabel, titleInput, titleError);

  // Description textarea
  const descField = el("div", { class: "pp-field" });
  const descLabel = el(
    "label",
    { class: "pp-label pp-label--required", for: "pp-description" },
    t.descriptionLabel,
  );
  const descInput = el("textarea", {
    class: "pp-textarea",
    id: "pp-description",
    placeholder: t.descriptionPlaceholder,
    required: "true",
    "aria-required": "true",
    rows: "4",
  });
  const descError = el("div", { class: "pp-field-error", "aria-live": "polite" });
  descError.style.display = "none";
  descField.append(descLabel, descInput, descError);

  // Screenshot upload
  const fileField = el("div", { class: "pp-field" });
  const fileLabelRow = el("div", {
    style: "display:flex;align-items:center;",
  });
  const fileLabel = el("span", { class: "pp-label" }, t.screenshotLabel);
  const fileBadge = el("span", { class: "pp-badge" }, "0");
  fileBadge.style.display = "none";
  fileLabelRow.append(fileLabel, fileBadge);

  const fileZone = el("div", { class: "pp-file-input" });
  fileZone.innerHTML = `${UPLOAD_ICON}<span class="pp-file-input__text">${t.screenshotDrop}</span><span class="pp-file-input__hint">${t.screenshotHint}</span>`;
  const fileInput = el("input", {
    type: "file",
    accept: "image/jpeg,image/png,image/webp",
    multiple: "true",
  });
  fileZone.appendChild(fileInput);

  const fileError = el("div", { class: "pp-field-error", "aria-live": "polite" });
  fileError.style.display = "none";
  const previewContainer = el("div", { class: "pp-file-previews" });

  fileField.append(fileLabelRow, fileZone, fileError, previewContainer);

  // Message area
  const messageEl = el("div", { class: "pp-message" });
  messageEl.style.display = "none";

  body.append(categoryField, titleField, descField, fileField, messageEl);

  // ---- Footer ----
  const footer = el("div", { class: "pp-form-footer" });
  const submitBtn = el("button", {
    class: "pp-btn-submit",
    type: "button",
  });
  submitBtn.textContent = t.submit;
  footer.appendChild(submitBtn);

  // Assemble
  container.append(header, body, footer);
  overlay.appendChild(container);
  shadowRoot.appendChild(overlay);

  // ---- Interaction handlers ----

  // Close
  const close = () => {
    if (sdkState.isOpen) {
      sdkState.isOpen = false;
      sdkState.emit("close");
    }
  };

  closeBtn.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  // Escape key (listen on the host document)
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && sdkState.isOpen) {
      close();
    }
  };
  // Clean up previous handler if any (from a re-init)
  destroyReportForm();
  document.addEventListener("keydown", onKeyDown);
  documentKeydownHandler = onKeyDown;

  // File zone click
  fileZone.addEventListener("click", () => fileInput.click());

  // Drag & drop
  fileZone.addEventListener("dragover", (e) => {
    e.preventDefault();
    fileZone.classList.add("pp-file-input--dragover");
  });
  fileZone.addEventListener("dragleave", () => {
    fileZone.classList.remove("pp-file-input--dragover");
  });
  fileZone.addEventListener("drop", (e) => {
    e.preventDefault();
    fileZone.classList.remove("pp-file-input--dragover");
    if (e.dataTransfer?.files) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  });

  fileInput.addEventListener("change", () => {
    if (fileInput.files) {
      addFiles(Array.from(fileInput.files));
    }
    // Reset so the same file can be re-selected
    fileInput.value = "";
  });

  // ---- File management ----

  function addFiles(incoming: File[]) {
    fileError.style.display = "none";

    for (const f of incoming) {
      if (files.length >= MAX_FILES) {
        showFileError(t.maxFiles);
        break;
      }
      if (!ACCEPTED_TYPES.includes(f.type)) {
        showFileError(t.invalidType);
        continue;
      }
      if (f.size > MAX_SIZE) {
        showFileError(t.maxSize);
        continue;
      }
      files.push(f);
    }

    renderPreviews();
  }

  function removeFile(index: number) {
    files.splice(index, 1);
    renderPreviews();
  }

  function renderPreviews() {
    previewContainer.innerHTML = "";
    fileBadge.textContent = String(files.length);
    fileBadge.style.display = files.length > 0 ? "inline-flex" : "none";

    files.forEach((f, i) => {
      const wrap = el("div", { class: "pp-file-preview" });
      const img = el("img", { alt: f.name });
      img.src = URL.createObjectURL(f);
      img.onload = () => URL.revokeObjectURL(img.src);

      const removeBtn = el("button", {
        class: "pp-file-preview__remove",
        type: "button",
        "aria-label": config.locale === "en" ? "Remove" : "삭제",
      });
      removeBtn.innerHTML = "&times;";
      removeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        removeFile(i);
      });

      wrap.append(img, removeBtn);
      previewContainer.appendChild(wrap);
    });
  }

  function showFileError(msg: string) {
    fileError.textContent = msg;
    fileError.style.display = "block";
  }

  // ---- Validation ----

  function validate(): boolean {
    let valid = true;

    if (!titleInput.value.trim()) {
      titleError.textContent = t.required;
      titleError.style.display = "block";
      valid = false;
    } else {
      titleError.style.display = "none";
    }

    if (!descInput.value.trim()) {
      descError.textContent = t.required;
      descError.style.display = "block";
      valid = false;
    } else {
      descError.style.display = "none";
    }

    return valid;
  }

  // Clear validation on input
  titleInput.addEventListener("input", () => {
    if (titleInput.value.trim()) titleError.style.display = "none";
  });
  descInput.addEventListener("input", () => {
    if (descInput.value.trim()) descError.style.display = "none";
  });

  // ---- Submit ----

  submitBtn.addEventListener("click", async () => {
    messageEl.style.display = "none";
    if (!validate()) return;

    sdkState.isSubmitting = true;
    sdkState.emit("submit-start");
    submitBtn.disabled = true;
    submitBtn.innerHTML = `<span class="pp-btn-submit__spinner"></span> ${t.submitting}`;

    try {
      const payload = {
        title: titleInput.value.trim(),
        description: descInput.value.trim(),
        userCategoryId: categorySelect.value
          ? Number(categorySelect.value)
          : undefined,
        reporterEmail: config.user?.email,
        reporterMeta: config.user ?? undefined,
        browserInfo: collectBrowserInfo(),
        pageUrl: location.href,
      };

      const ticket = await apiClient.submitTicket(payload);

      // Upload screenshots in parallel
      if (files.length > 0) {
        await Promise.all(
          files.map((f) => apiClient.uploadScreenshot(ticket.id, f)),
        );
      }

      sdkState.isSubmitting = false;
      sdkState.emit("submit-success");

      // Show success
      showMessage("success", t.successMessage);
      resetForm();

      // Auto-close after 2 seconds
      setTimeout(() => {
        messageEl.style.display = "none";
        close();
      }, 2000);
    } catch (err) {
      sdkState.isSubmitting = false;
      sdkState.emit("submit-error", err instanceof Error ? err : new Error(String(err)));
      showMessage("error", t.errorMessage);
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = t.submit;
    }
  });

  function showMessage(type: "success" | "error", text: string) {
    messageEl.className = `pp-message pp-message--${type}`;
    messageEl.textContent = text;
    messageEl.style.display = "block";
  }

  function resetForm() {
    titleInput.value = "";
    descInput.value = "";
    categorySelect.value = "";
    files.length = 0;
    renderPreviews();
    titleError.style.display = "none";
    descError.style.display = "none";
    fileError.style.display = "none";
  }

  // ---- State listeners ----

  const onOpen = () => {
    overlay.classList.add("pp-form-overlay--visible");
    // Focus management: focus the title input
    setTimeout(() => titleInput.focus(), 100);
  };

  const onClose = () => {
    // Blur focused element to dismiss iOS keyboard before hiding the overlay.
    // Without this, iOS Safari's visual viewport stays shifted after close.
    const focused = shadowRoot.activeElement ?? document.activeElement;
    if (focused instanceof HTMLElement) {
      focused.blur();
    }
    overlay.classList.remove("pp-form-overlay--visible");
    messageEl.style.display = "none";
    // Force iOS Safari to recalculate viewport position
    window.scrollTo(0, window.scrollY);
  };

  sdkState.on("open", onOpen);
  sdkState.on("close", onClose);

  // Sync with initial state
  if (sdkState.isOpen) {
    onOpen();
  }

  return overlay;
}

// ---------------------------------------------------------------------------
// Helpers (async, non-blocking)
// ---------------------------------------------------------------------------

async function loadCategories(
  apiClient: ApiClient,
  selectEl: HTMLSelectElement,
): Promise<void> {
  try {
    const categories = await apiClient.getCategories();
    sdkState.categories = categories;
    for (const cat of categories) {
      const opt = document.createElement("option");
      opt.value = String(cat.id);
      opt.textContent = cat.name;
      if (cat.description) {
        opt.title = cat.description;
      }
      selectEl.appendChild(opt);
    }
  } catch {
    // Categories are optional — silently ignore
  }
}
